import { useState, useCallback } from 'react';
import { UserService } from '@sdk-point/talisis';
import { useB2BPermissions } from '../../../../context/B2BPermissionsContext';
import { parseScopeType } from '../../../../utils/scopeType';

const UserServiceSDK = new UserService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);

const PAGE_SIZE = 10;

export default function useStudentSearch() {
    const { assignedCompanyName, scopeType } = useB2BPermissions();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(1);

    const searchStudents = useCallback(async (filters = [], currentPage = 1, companyTag = null) => {
        setLoading(true);
        setError(null);
        setPage(currentPage);

        try {
            // Clean filters: remove 'label' key before sending to SDK
            const filtersToSend = filters.map(filter => {
                const clean = {};
                Object.keys(filter).forEach(key => {
                    if (key !== 'label') clean[key] = filter[key];
                });
                return clean;
            }).filter(f => Object.keys(f).length > 0);

            // Company scope sent to the /users lambda. The lambda accepts MULTIPLE
            // companies AND MULTIPLE scope types: it reads one or more `scope_type`
            // plus a repeated `empresa_tag` query param
            // (e.g. &scope_type=internal&scope_type=external&empresa_tag=Chedraui&empresa_tag=crehana).
            //
            // `scopeType` from the context is a CSV string ("internal",
            // "external" or "internal, external"). It is parsed and sent as a
            // repeated query param — one `scope_type` per active type — mirroring how
            // `empresa_tag` is sent (one object per value). This keeps serialization
            // consistent and avoids pushing a single "internal, external" token the
            // lambda might not split.
            //
            // Behaviour:
            //  - `companyTag` null/empty ("Todas") → send EVERY company the user has
            //    configured in their scope (assignedCompanyName).
            //  - `companyTag` set → send ONLY that company (user narrowed the search).
            //
            // Either way the search stays within the user's scope: the selector is
            // fed only with the user's own assigned companies.
            //
            // The SDK's getAll serializes each {key: value} pair as `&key=value`,
            // so pushing several {empresa_tag} objects produces the repeated param.
            const companiesToSend = companyTag
                ? [companyTag]
                : assignedCompanyName;

            if (companiesToSend.length > 0) {
                parseScopeType(scopeType).forEach((type) => {
                    filtersToSend.push({ scope_type: type });
                });
                companiesToSend.forEach((name) => {
                    if (name) filtersToSend.push({ empresa_tag: name });
                });
            }

            const requests = await UserServiceSDK.getAll(currentPage, filtersToSend, PAGE_SIZE);
            setStudents(requests.data || []);
            setTotalPages(requests.totalPages || 0);
        } catch (err) {
            const message = (err && err.response && err.response.data && err.response.data.message)
                || (err && err.message)
                || (typeof err === 'string' ? err : 'Error en la búsqueda');
            setError(message);
            setStudents([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [assignedCompanyName, scopeType]);

    const clearResults = useCallback(() => {
        setStudents([]);
        setError(null);
        setTotalPages(0);
        setPage(1);
    }, []);

    return {
        students,
        loading,
        error,
        totalPages,
        page,
        setPage,
        searchStudents,
        clearResults,
    };
}
